import './fontFamily.css';
import { FC, PropsWithChildren, useMemo } from 'react';
import {
  createBrowserRouter,
  matchRoutes,
  RouterProvider,
  useLocation,
  useRoutes,
} from 'react-router-dom';
import { Badge, Menu, theme } from 'antd';
import Layout, { Content } from 'antd/es/layout/layout';
import {
  ApiClientProvider,
  NavigationBlockingProvider,
  cleanArray,
  ConfigurationProvider,
  CsrfTokenProvider,
  Environment,
  NavigateAwayPrompt,
  QueryClientProvider,
  RequireAuth,
  Tooltip,
  useConfiguration,
  UserSelectBlockingProvider,
  AlertProvider,
  AlertToast,
  NotificationProvider,
} from '@atlas-frontend/sdk';
import atlasLogoSymbolOnly from './assets/img/eesti-pank-logo-symbol-only.svg';
import styled, { createGlobalStyle } from 'styled-components';
import {
  AtlasThemeProvider,
  colorGray,
  colorNeutralBg,
  colorText,
  components,
  fontWeightNormal,
} from '@atlas-frontend/design-tokens';
import Sider from 'antd/es/layout/Sider';
import { I18nProvider } from '@atlas-frontend/i18n';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import 'dayjs/locale/et';
import AppErrorBoundary from './errors/AppErrorBoundary';
import { useMainSections } from './useMainSections';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import '@ant-design/v5-patch-for-react-19';

const SIDE_MENU_PADDING_BLOCK_PX = 18;
const SIDE_MENU_ICON_SIZE_PX = 18;
const LOGO_SIZE_PX = 32;
const DEBUG_REACT_QUERY =
  (import.meta.env.VITE_DEBUG_REACT_QUERY &&
    import.meta.env.VITE_DEBUG_REACT_QUERY !== 'false' &&
    import.meta.env.VITE_DEBUG_REACT_QUERY !== '0') ??
  true;

export const App: FC = () => (
  <AppErrorBoundary>
    <I18nProvider>
      <ApiClientProvider>
        <QueryClientProvider>
          {DEBUG_REACT_QUERY && <ReactQueryDevtools initialIsOpen={false} />}
          <AtlasThemeProvider>
            <NotificationProvider>
              <RouterProvider
                router={createBrowserRouter([
                  {
                    path: '/*',
                    element: (
                      <AppErrorBoundary>
                        <RequireAuth>
                          <CsrfTokenProvider>
                            <ConfigurationProvider>
                              <NavigationBlockingProvider>
                                <UserSelectBlockingProvider>
                                  <AlertProvider>
                                    <HelmetProvider>
                                      <Helmet>
                                        <title>Atlas</title>
                                      </Helmet>
                                      <AppMain />
                                      <NavigateAwayPrompt />
                                      <AlertToast />
                                    </HelmetProvider>
                                  </AlertProvider>
                                </UserSelectBlockingProvider>
                              </NavigationBlockingProvider>
                            </ConfigurationProvider>
                          </CsrfTokenProvider>
                        </RequireAuth>
                      </AppErrorBoundary>
                    ),
                  },
                ])}
              />
            </NotificationProvider>
          </AtlasThemeProvider>
        </QueryClientProvider>
      </ApiClientProvider>
    </I18nProvider>
  </AppErrorBoundary>
);

const AppMain: FC = () => {
  const location = useLocation();
  const { environment } = useConfiguration();
  const { routes, sections, sectionsByRoute } = useMainSections();

  const mainMenuItems = useMemo(
    () =>
      sections
        .filter(
          ({ hiddenFromMenu, key }) =>
            !hiddenFromMenu &&
            (key !== 'settings' || (environment && environment !== 'prod')),
        )
        .map(({ route, ...menuItem }) => menuItem),
    [environment, sections],
  );

  const selectedKeys = useMemo(
    () =>
      cleanArray(
        matchRoutes(routes, location)?.map(({ route }) =>
          sectionsByRoute?.get(route)?.key.toString(),
        ),
      ),
    [location, routes, sectionsByRoute],
  );

  // Known issue: https://github.com/ant-design/ant-design/issues/40531
  const { colorBgContainer } = theme.useToken().token;

  return (
    <AppLayout id="app-layout" $background={colorBgContainer}>
      <MenuPopupStyles />
      <EnvironmentRibbon
        text={
          COMMIT_HASH ? (
            <Tooltip title={COMMIT_HASH}>{environment}</Tooltip>
          ) : (
            environment
          )
        }
        color={environmentColors[environment]}
      >
        <StyledSider
          id="main-sider"
          collapsed
          collapsedWidth={`${components.Menu.collapsedWidth}px`}
        >
          <AtlasLogoSymbol />
          <SideMenu
            mode="inline"
            triggerSubMenuAction="click"
            items={mainMenuItems}
            selectedKeys={selectedKeys}
          />
        </StyledSider>
      </EnvironmentRibbon>
      <Layout>
        <AppErrorBoundary key={location.pathname}>
          <Content>{useRoutes(routes)}</Content>
        </AppErrorBoundary>
      </Layout>
    </AppLayout>
  );
};

const environmentColors: Partial<Record<Environment, string>> = {
  local: 'red',
  dev: 'orange',
  test: 'purple',
};
const EnvironmentRibbon = styled(Badge.Ribbon)`
  z-index: 99;
  inset-block-start: 5px;
`;
const StyledSider = styled(Sider)`
  height: 100vh;
`;

const AtlasLogoSymbol = styled((({ className, children }) => (
  <div id="app-logo" className={className}>
    <div>{children}</div>
  </div>
)) as FC<PropsWithChildren<{ className?: string }>>)`
  width: ${LOGO_SIZE_PX}px;

  > div {
    background-image: url(${atlasLogoSymbolOnly});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    height: 0;
    width: 100%;
    padding-top: 100%;
  }
`;

const AppLayout = styled(Layout)<{ $background: string }>`
  min-height: 100vh;

  aside {
    background: ${(props) => props.$background} !important;
    padding-block: ${SIDE_MENU_PADDING_BLOCK_PX}px;

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
`;

const SideMenu = styled(Menu)`
  margin-top: 40px;
  border-inline: none !important;
  width: 100% !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  [data-menu-id$='visual-validation'] + li {
    margin-top: auto;
  }

  .ant-menu-item-icon {
    width: ${SIDE_MENU_ICON_SIZE_PX}px;
    height: ${SIDE_MENU_ICON_SIZE_PX}px;
    // 16px is the default font size of antd
    margin: ${(16 - SIDE_MENU_ICON_SIZE_PX) / 2}px !important;
  }

  .ant-menu-item,
  .ant-menu-submenu > .ant-menu-submenu-title {
    color: ${colorGray[7]} !important;
    background-color: transparent !important;

    &:hover,
    &.ant-menu-item-selected {
      color: ${colorText} !important;
      background-color: ${colorNeutralBg} !important;
    }
  }
`;

const MenuPopupStyles = createGlobalStyle`
  .side-menu-popup {

    .ant-menu-submenu-selected > .ant-menu-submenu-title {
      color: ${colorText} !important;
      background-color: ${colorNeutralBg} !important;
    }

    .ant-menu-submenu-title,
    .ant-menu-item {
      color: ${colorGray[7]} !important;

      &:hover {
        color: ${colorText} !important;
        background-color: ${colorNeutralBg} !important;
      }
    }
  }
  .ant-tree-select-dropdown
  .ant-select-tree
  .ant-select-tree-title {
    font-weight: ${fontWeightNormal} !important;
    color: ${colorText} !important;
  }
`;
